'use client';

import { createContext, ReactNode, useContext, useEffect } from "react";
import { AuthenticPlace, AuthenticPlaceInput, PublicationStatus } from "@/app/lib/generated/graphql-types";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { AuthenticPlaceEditable } from "@/app/features/sites/authentic-places/types/types";
import { AuthenticPlaceSchema } from "@/app/features/sites/authentic-places/types/schema";
import { getAuthenticPlace } from "@/app/features/sites/authentic-places/data-access/queries";
import { updateAuthenticPlace } from "@/app/features/sites/authentic-places/data-access/mutations";
import {
    useCollectionTypeEditor, UseCollectionTypeEditorType
} from "@/app/lib/hooks/use-editor/useCollectionTypeEditor";
import { useSiteAuthenticPlacesEditorContext } from "@/app/features/sites/authentic-places/contexts/SiteAuthenticPlaceEditorContext";

const AuthenticPlaceEditorContext = createContext<UseCollectionTypeEditorType<AuthenticPlaceEditable> | undefined>(undefined);

export function useAuthenticPlaceEditorContext() {
    const context = useContext(AuthenticPlaceEditorContext);
    if (!context) {
        throw new Error("useAuthenticPlaceEditorContext must be used within a AuthenticPlaceEditorProvider");
    }
    return context;
}

interface AuthenticPlaceEditorProviderProps {
    documentId: string;
    children: ReactNode;
}

export default function AuthenticPlaceEditorProvider({ documentId, children }: AuthenticPlaceEditorProviderProps) {
    const { registerChildEditor, unregisterChildEditor } = useSiteAuthenticPlacesEditorContext();
    const editor = useCollectionTypeEditor<AuthenticPlace, AuthenticPlaceEditable, AuthenticPlaceInput>({
        documentId,
        schema: AuthenticPlaceSchema,
        loadEntity: getAuthenticPlace,
        update: updateAuthenticPlace,
        autosave: async (targetDocumentId, input) => {
            return await updateAuthenticPlace({
                documentId: targetDocumentId,
                data: input,
                status: PublicationStatus.Draft,
            });
        },
    });

    useEffect(() => {
        registerChildEditor(
            documentId,
            {
                hasChanges: editor.hasChanges,
                autosaveState: editor.autosaveState,
            },
            {
                publish: editor.publish,
                discardChanges: editor.discardChanges,
            }
        );

        return () => {
            unregisterChildEditor(documentId);
        };
    }, [
        documentId,
        editor.hasChanges,
        editor.autosaveState,
        editor.publish,
        editor.discardChanges,
        registerChildEditor,
        unregisterChildEditor,
    ]);

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <AuthenticPlaceEditorContext.Provider value={editor}>
            {children}
        </AuthenticPlaceEditorContext.Provider>
    )
}
